import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Sparkles, Check, Timer } from "lucide-react";
import LevelKaarten from "@/components/LevelKaarten";
import SpeelruimteBanner from "@/components/SpeelruimteBanner";
import { SquiggleUnderline, FloatingBlob } from "@/components/Squiggle";
import usePageMeta from "@/hooks/usePageMeta";

const MINI_OEFENINGEN = [
  {
    k: "adem",
    tijd: "1 minuut",
    naam: "Eén-minuut ankeren",
    tint: "#97B79D",
    stappen: [
      "Zet je voeten plat op de grond.",
      "Adem vier tellen in, zes tellen uit — drie keer.",
      "Merk één ding op dat je nu voelt. Zonder oordeel.",
    ],
  },
  {
    k: "vonk",
    tijd: "3 minuten",
    naam: "Vonk-scan",
    tint: "#F0B98D",
    stappen: [
      "Denk terug aan afgelopen week.",
      "Wanneer voelde iets — al was het kort — lichter?",
      "Doe dat vandaag opnieuw. Vijf minuten volstaat.",
    ],
  },
  {
    k: "rem",
    tijd: "2 minuten",
    naam: "Één-woord ruil",
    tint: "#E06B3F",
    stappen: [
      "Denk aan een zin die je vaak tegen jezelf zegt.",
      "Vervang één woord. 'Moet' wordt 'mag'. 'Kan niet' wordt 'nog niet'.",
      "Zeg de nieuwe zin drie keer hardop.",
    ],
  },
  {
    k: "speel",
    tijd: "5 minuten",
    naam: "Ander been",
    tint: "#78A85A",
    stappen: [
      "Kies iets kleins dat je vandaag doet.",
      "Doe het op een verkeerde manier. Op één been. Andere hand. Terug lezen.",
      "Vraag jezelf: waar zit hier speelruimte?",
    ],
  },
];

export default function Probeer() {
  usePageMeta({
    title: "Probeer LevelLeven",
    description: "Vier minuten. Vier oefeningen. Geen test — één stap.",
  });
  const [open, setOpen] = useState(null);

  return (
    <div className="bg-ll-cream text-ll-ink">
      <SpeelruimteBanner
        eyebrow="Probeer LevelLeven"
        headline={
          <>
            Vier minuten.
            <br />
            Vier{" "}
            <span className="italic text-ll-coral relative inline-block">
              oefeningen
              <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
            </span>.
          </>
        }
        belofte="Geen test. Één stap."
        subhead="Kleine dingen uit de LevelLeven-methodiek die je nu, op deze pagina, kunt doen. Sluit je ogen niet — dit werkt met ze open."
        chips={["Voor iedereen", "Geen account", "Geen download nodig", "Doe wat blijft plakken"]}
        ctaPrimary={{ label: "Begin met de eerste", to: "#oefeningen" }}
        ctaSecondary={{ label: "Of trek een kaart", to: "#kaarten" }}
        images={[
          "https://images.unsplash.com/photo-1518611012118-696072aa579a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
          "https://images.unsplash.com/photo-1502680390469-be75c86b636f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
          "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
          "https://images.unsplash.com/photo-1522163182402-834f871fd851?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
        ]}
        tone="yellow"
      />

      {/* MINI OEFENINGEN */}
      <section id="oefeningen" className="py-24 sm:py-32 relative">
        <FloatingBlob color="#F0B98D" size={140} className="top-10 right-10 opacity-70" />
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-2xl mb-14">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Klein doen · nu meteen</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              Klik open. Doe 'm.{" "}
              <span className="italic text-ll-coral">Meer hoeft niet.</span>
            </h2>
          </div>
          <div className="space-y-3">
            {MINI_OEFENINGEN.map((o, i) => {
              const isOpen = open === o.k;
              return (
                <div
                  key={o.k}
                  className="rounded-3xl border border-ll-ink/12 bg-white overflow-hidden"
                  data-testid={`oefening-${o.k}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : o.k)}
                    className="w-full flex items-center gap-6 p-6 sm:p-8 text-left hover:bg-ll-ink/[0.02] transition-colors"
                  >
                    <span
                      className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: o.tint }}
                    >
                      <span className="chapter-num text-lg text-ll-ink">{String(i + 1).padStart(2, "0")}</span>
                    </span>
                    <div className="flex-1">
                      <p className="font-heading text-2xl sm:text-3xl italic font-light" style={{ color: o.tint }}>
                        {o.naam}
                      </p>
                      <p className="mt-1 text-xs uppercase tracking-[0.28em] text-ll-navy/60 flex items-center gap-2">
                        <Timer size={12} /> {o.tijd}
                      </p>
                    </div>
                    <ArrowUpRight
                      size={22}
                      className={`text-ll-navy/60 transition-transform ${isOpen ? "rotate-90" : ""}`}
                    />
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 sm:px-8 pb-8">
                          <ol className="space-y-3 border-t border-ll-ink/10 pt-6">
                            {o.stappen.map((s, si) => (
                              <li key={si} className="flex gap-4">
                                <span className="chapter-num text-sm shrink-0 w-8" style={{ color: o.tint }}>
                                  {String(si + 1).padStart(2, "0")}
                                </span>
                                <p className="text-ll-navy/90 leading-relaxed">{s}</p>
                              </li>
                            ))}
                          </ol>
                          <div className="mt-6 pt-4 border-t border-ll-ink/10 flex items-center gap-2 text-sm italic text-ll-navy/70">
                            <Check size={14} className="text-ll-coral" />
                            Dat was 'm. Je hoeft er niets mee. Alleen: je hebt het gedaan.
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VONKVINDER BRIDGE */}
      <section className="py-16 sm:py-20 relative overflow-hidden">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10">
          <Link
            to="/vonkvinder"
            data-testid="probeer-vonkvinder-link"
            className="group block rounded-[36px] bg-ll-navy text-ll-cream p-10 sm:p-14 hover:bg-ll-coral transition-colors relative overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-8 items-center">
              <div className="sm:col-span-9">
                <p className="text-[10px] uppercase tracking-[0.35em] text-ll-yellow">2 minuten · geen data</p>
                <h3 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.02] font-light">
                  Weet je niet waar te beginnen?{" "}
                  <span className="italic text-ll-yellow group-hover:text-ll-cream transition-colors">Vind eerst je vonk.</span>
                </h3>
              </div>
              <div className="sm:col-span-3 flex sm:justify-end">
                <motion.div
                  animate={{ rotate: [0, 8, -8, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="w-24 h-24 rounded-full flex items-center justify-center"
                  style={{
                    background: "radial-gradient(circle at 30% 30%, #F0B98D 0%, #E06B3F 80%)",
                    boxShadow: "0 0 40px #F0B98D77",
                  }}
                >
                  <Sparkles size={30} strokeWidth={1.5} className="text-ll-ink" />
                </motion.div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* LEVELLEVEN KAARTEN */}
      <div id="kaarten">
        <LevelKaarten />
      </div>
    </div>
  );
}
